import * as z from 'zod';
import { useEffect } from 'react';
import { gql } from '@apollo/client';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useMutation } from '@apollo/client/react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CircularProgress from '@mui/material/CircularProgress';

import { useTranslate } from 'src/locales';
import { useSelector, useDispatch } from 'src/state';
import * as ActionTypes from 'src/state/action-types';
import { createFolder } from 'src/state/folder/actions';
import { selectWorkspaceId } from 'src/state/workspace/selectors';
import { selectCurrentFolderId } from 'src/state/content-browser/selectors';
import { info, error, primary, success, warning, secondary } from 'src/theme/core';

import { toast } from 'src/components/snackbar';
import { Form, Field } from 'src/components/hook-form';
import { ColorPicker } from 'src/components/color-utils';

// ----------------------------------------------------------------------

const GET_FOLDER_QUERY = gql`
  query getFolder($folderId: String!) {
    getFolder(folderId: $folderId) {
      id
      name
      color
    }
  }
`;

const UPDATE_FOLDER_MUTATION = gql`
  mutation updateFolder($input: FolderUpdateInput!) {
    updateFolder(input: $input) {
      id
      name
      color
      updated_at
    }
  }
`;

const FolderSchema = (t: (key: string) => string) =>
  z.object({
    name: z
      .string()
      .trim()
      .min(1, { message: t('Folder name is required') })
      .max(140, { message: t('Folder name must be less than 140 characters') }),
    color: z.string().min(1, { message: t('Color is required') }),
  });

export type FolderSchemaType = z.infer<ReturnType<typeof FolderSchema>>;

const FOLDER_COLOR_OPTIONS = [
  primary.main,
  secondary.main,
  info.main,
  info.darker,
  success.main,
  warning.main,
  error.main,
  error.darker,
];

const defaultValues: FolderSchemaType = {
  name: '',
  color: FOLDER_COLOR_OPTIONS[0],
};

type FolderFormProps = {
  folderId?: string;
  onClose: () => void;
  onSuccess?: () => Promise<void> | void;
};

// ----------------------------------------------------------------------

export function FolderForm({ folderId, onClose, onSuccess }: FolderFormProps) {
  const { t } = useTranslate();
  const dispatch = useDispatch();
  const isEditMode = Boolean(folderId);

  const workspaceId = useSelector(selectWorkspaceId);
  const parentFolderId = useSelector(selectCurrentFolderId);

  const { data: folderData, loading: folderLoading } = useQuery<{
    getFolder: { id: string; name: string; color: string };
  }>(GET_FOLDER_QUERY, {
    variables: { folderId },
    skip: !folderId,
  });

  const methods = useForm<FolderSchemaType>({
    mode: 'all',
    resolver: zodResolver(FolderSchema(t)),
    defaultValues,
  });

  const {
    control,
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  useEffect(() => {
    if (folderData?.getFolder) {
      reset({
        name: folderData.getFolder.name,
        color: folderData.getFolder.color || FOLDER_COLOR_OPTIONS[0],
      });
    }
  }, [folderData, reset]);

  const [updateFolder, { loading: isUpdating }] = useMutation(UPDATE_FOLDER_MUTATION, {
    refetchQueries: ['Search'],
  });

  const handleCloseDialog = () => {
    reset(defaultValues);
    onClose();
  };

  const onSubmit = handleSubmit(async (values) => {
    try {
      if (isEditMode && folderId) {
        await updateFolder({
          variables: {
            input: {
              id: folderId,
              name: values.name,
              color: values.color,
            },
          },
        });

        // Optimistically update Redux state
        dispatch({
          type: ActionTypes.FOLDER_UPDATE_SUCCESS,
          payload: {
            id: folderId,
            name: values.name,
            color: values.color,
          },
        });

        toast.success(t('Folder has been updated.'));
      } else {
        await dispatch(
          createFolder({
            name: values.name,
            color: values.color,
            parentId: parentFolderId,
            workspaceId,
          })
        );
        toast.success(t('Folder has been created.'));
      }

      if (onSuccess) {
        await onSuccess();
      }

      handleCloseDialog();
    } catch (error2) {
      console.error(error2);
      toast.error(isEditMode ? t('Unable to update folder') : t('Unable to create folder'));
    }
  });

  if (folderLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 8,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const isLoading = isSubmitting || isUpdating;

  return (
    <Form methods={methods} onSubmit={onSubmit}>
      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <Field.Text name="name" label={t('Folder Name')} autoFocus />

          <Stack spacing={1}>
            <Typography variant="subtitle2">{t('Color')}</Typography>
            <Controller
              name="color"
              control={control}
              render={({ field }) => (
                <ColorPicker
                  value={field.value}
                  options={FOLDER_COLOR_OPTIONS}
                  onChange={(color) => field.onChange(color as string)}
                />
              )}
            />
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={handleCloseDialog}>
          {t('Cancel')}
        </Button>
        <Button type="submit" variant="contained" loading={isLoading} disabled={isLoading}>
          {isEditMode ? t('Update') : t('Create')}
        </Button>
      </DialogActions>
    </Form>
  );
}
